export const isAssessmentPack = (pack) => pack?.pack_mode === "assessment_simulation";
const text = (value) => typeof value === "string" ? value : "";
const questionsOf = (pack) => isAssessmentPack(pack) ? pack.questions || [] : (pack.topics || []).flatMap((topic) => topic.questions || []);

export function editingUnavailable(pack) {
  if (String(pack.status).toLowerCase() !== "completed" || ["published", "public"].includes(pack.pack_type) || pack.is_public === true || pack.visibility === "public") return "This pack is not ready to edit.";
  if (!Number.isInteger(pack.revision) || pack.revision < 1) return "Editing is unavailable because this pack is missing its saved revision. Reload the latest version.";
  const questions = questionsOf(pack);
  if (isAssessmentPack(pack)) {
    const ids = questions.map((question) => question.question_id);
    if (ids.some((id) => typeof id !== "string" || !id.trim()) || new Set(ids).size !== ids.length) return "Editing is unavailable because this pack has missing or invalid question IDs. Reload the latest version.";
  }
  return questions.length ? "" : "This pack has no questions to edit.";
}

export function makeSimulatorDraft(pack) {
  const assessment = isAssessmentPack(pack);
  let number = 0;
  return (assessment ? [{questions: pack.questions}] : pack.topics || []).map((topic) => ({
    title: topic.title,
    questions: (topic.questions || []).map((question) => ({
      original: question,
      number: ++number,
      question: text(question.question),
      purpose: text(question.purpose),
      points: (assessment ? question.expected_points || [] : question.expected_answer_points || []).map(text),
      answer_match_mode: question.answer_match_mode ?? "semantic",
    })),
  }));
}

export function validateSimulatorDraft(pack, draft) {
  const assessment = isAssessmentPack(pack), errors = {};
  const rows = draft.flatMap((topic) => topic.questions);
  const originals = questionsOf(pack), wording = new Map();
  const retained = rows.map((row) => originals.indexOf(row.original));
  const validText = (value) => typeof value === "string" && value.trim().length > 0 && value.length <= 1000;
  let summary = editingUnavailable(pack);
  if (retained.some((index, position) => index < 0 || (position > 0 && index <= retained[position - 1]))) summary = "Keep existing questions in their original order.";
  if (!rows.length || (assessment && rows.length > 10)) summary = "Keep between 1 and 10 assessment questions.";
  if (!assessment && draft.some((topic) => !topic.questions.length || topic.questions.length > 15)) summary = "Each material topic must contain between 1 and 15 questions.";
  for (const row of rows) {
    const messages = [];
    if (!originals.includes(row.original)) messages.push("Use only existing questions from this pack.");
    if (!validText(row.question)) messages.push("Question wording must contain 1–1,000 characters.");
    if (assessment && !validText(row.purpose)) messages.push("Purpose must contain 1–1,000 characters.");
    if (!row.points.length || row.points.length > 10) messages.push("Keep between 1 and 10 expected points.");
    if (row.points.some((point) => !validText(point))) messages.push("Each expected point must contain 1–1,000 characters.");
    const points = row.points.map((point) => point.trim().toLowerCase());
    if (new Set(points).size !== points.length) messages.push("Expected points must not be duplicates.");
    if (!["semantic", "exact"].includes(row.answer_match_mode)) messages.push("Choose a valid answer matching mode.");
    const normalized = row.question.trim().replace(/\s+/g, " ").toLowerCase();
    if (wording.has(normalized)) {
      messages.push("Question wording must not duplicate another question.");
      const previous = wording.get(normalized);
      errors[previous] = [...(errors[previous] || []), "Question wording must not duplicate another question."];
    } else wording.set(normalized, row.number);
    errors[row.number] = [...(errors[row.number] || []), ...messages];
  }
  return {summary, errors, invalid: !!summary || Object.values(errors).some((messages) => messages.length)};
}

export function simulatorEditPayload(pack, draft) {
  const fields = (row) => ({question: row.question, answer_match_mode: row.answer_match_mode});
  if (isAssessmentPack(pack)) return {revision: pack.revision, questions: draft[0].questions.map((row) => ({
    question_id: row.original.question_id, ...fields(row), purpose: row.purpose, expected_points: [...row.points],
  }))};
  return {revision: pack.revision, title: pack.title, topics: draft.map((topic, index) => ({
    title: pack.topics[index].title,
    questions: topic.questions.map((row) => ({...fields(row), expected_answer_points: [...row.points]})),
  }))};
}
